import {
  Checkbox,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiArrowLeft, HiDotsVertical, HiHome } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  resetExplorerToInitialState,
  selectCurrentFiles,
  selectCurrentFolder,
  selectLoadingFolder,
  setLoadingFolder,
} from "../../state/explorer/explorerSlice";
import BottomDrawer from "../bottomSheet/BottomDrawer";
import LoaderMenu from "../menu/LoaderMenu";
import {
  BOTTOM_DRAWER_QUICK_ACTIONS_CONFIG,
  LOADER_MENU_CONFIG,
} from "../menu/menus";
import "./Breadcrumb.css";
import { FolderDto } from "@braoshzmvavz/picnube-common";
import { selectCurrentSpace } from "src/state/profile/profileSlice";
import BreadcrumbDrawer from "./BreadcrumbDrawer";

interface BreadcrumbNode {
  folderId: string;
  folderName: string;
}

const useResizeEffect = (onResize, deps = []) => {
  useEffect(() => {
    onResize();
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, onResize]);
};

export default function Breadcrumb({
  onActionClick,
  setIsLoaderOverlay,
  selectedFiles,
  setSelectedFiles,
  demo,
  visible,
}) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const computedColorScheme = useComputedColorScheme("dark");

  const dispatch = useAppDispatch();
  const folder = useAppSelector(selectCurrentFolder);
  const loadingFolder = useAppSelector(selectLoadingFolder) as FolderDto | null;
  const currentFiles = useAppSelector(selectCurrentFiles);
  const currentSpace = useAppSelector(selectCurrentSpace);

  // Overflow
  const [overflowActive, setOverflowActive] = useState(false);
  const [container, setContainer] = useState(null);
  const breadcrumbRef = useRef(container);
  breadcrumbRef.current = container;

  const setOverflowActiveOnResize = useCallback(() => {
    if (!breadcrumbRef.current) return;
    const { clientWidth, scrollWidth } = breadcrumbRef.current;
    setOverflowActive(scrollWidth > clientWidth);
  }, [breadcrumbRef]);

  useResizeEffect(setOverflowActiveOnResize, [breadcrumbRef]);
  // -------

  useEffect(() => {
    if (!folder && !loadingFolder) {
      setItems([]);
      return;
    }
    
    const currentFolder = loadingFolder || folder;
    if (!currentFolder.path) {
      setItems([]);
      return;
    }
    const breadcrumbNodes = currentFolder.path.split(";").map((node: string) => {
      const splitNode = node.split(":");
      return { folderId: splitNode[0], folderName: splitNode[1] };
    });
    setItems(breadcrumbNodes);
  }, [folder, loadingFolder]);

  const extractPathOfSelectedFolderFromTheCurrentFolderPath = (folderPath: string, selectedFolderId: string) => {
    const pathParts = folderPath.split(";");
    const selectedFolderIndex = pathParts.findIndex(part => part.startsWith(selectedFolderId + ":"));
    return pathParts.slice(0, selectedFolderIndex + 1).join(";");
  }

  const onBreadcrumbItemClick = (item: BreadcrumbNode) => {
    if (item.folderId === (folder as FolderDto)?.id) {
      return;
    }
    if (loadingFolder) {
      return;
    }

    const folderPlaceholderForBreadcrumb = {
      id: item.folderId,
      name: item.folderName,
      path: extractPathOfSelectedFolderFromTheCurrentFolderPath(folder.path, item.folderId),
    } as FolderDto;

    dispatch(setLoadingFolder(folderPlaceholderForBreadcrumb));
    navigate((demo ? "/demo/browse/" : "/browse/") + item.folderId);
  };

  const navigateHome = () => {
    if (window.location.pathname.endsWith("browse")) {
      return;
    }
    dispatch(resetExplorerToInitialState());
    navigate(demo ? "/demo/browse" : "/browse");
  };

  const onBackClick = () => {
    const index = items.length - 2;

    if (index === -1) {
      navigateHome();
    }
    if (index < 0) {
      return;
    }

    onBreadcrumbItemClick(items[index]);
  };

  const renderHomeBreadcrumbNode = () => (
    <div
      key={"0-home"}
      onClick={navigateHome}
      className="cursor-pointer hover:text-blue-400"
    >
      {<HiHome></HiHome>}
    </div>
  );

  const renderBreadcrumbNode = (item: BreadcrumbNode, index: number) => {
    return (
      <div
        onClick={() => onBreadcrumbItemClick(item)}
        className="cursor-pointer hover:text-blue-400"
      >
        {item.folderName}
      </div>
    );
  };

  const renderBreadcrumbDropdown = (target, props) => {
    return (
      <BreadcrumbDrawer
        items={items}
        onItemClick={onBreadcrumbItemClick}
        targetElement={target}
      />
    );
  };

  const renderOverflowedBreadcrumb = () => {
    const index = items.length - 1;
    const item = items[index];

    const target = (
      <div className="flex flex-row gap-2 cursor-pointer hover:text-blue-400">
        <div
          key={"breadcrumbNode-separator--overflow"}
          className="flex gap-2 hover:text-blue"
        >
          / ...
        </div>
        <div className="hover:text-blue flex flex-row gap-2">
          <div>/</div>
          <div>{item?.folderName}</div>
        </div>
      </div>
    );

    return renderBreadcrumbDropdown(target, {
      width: 500,
      offset: 10,
      position: "bottom",
    });
  };

  const renderSelectedFilesText = () => {
    if (selectedFiles === null) {
      return null;
    }

    return `(${selectedFiles.length} ${
      selectedFiles.length > 1 ? "files" : "file"
    } selected)`;
  };

  const renderContent = () => {
    if (selectedFiles === null || selectedFiles.length > 0) {
      return (
        <div className="relative flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden">
          <div
            className={`flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden ${
              overflowActive ? "invisible" : "visible"
            }`}
            ref={(element) => {
              if (!element) return;
              setContainer(element);
              const { clientWidth, scrollWidth } = element;
              setOverflowActive(scrollWidth > clientWidth);
            }}
          >
            <div className="flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden">
              <Checkbox
                className="text-blue-400 cursor-pointer"
                onClick={() => {
                  selectedFiles == null
                    ? setSelectedFiles([])
                    : setSelectedFiles(null);
                }}
              />
              {"Select all"}
            </div>

            {renderSelectedFilesText()}
          </div>
        </div>
      );
    }
    return (
      <div className="relative flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden">
        {/* DEFAULT BREADCRUMB */}
        <div
          className={`flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden ${
            overflowActive ? "invisible" : "visible"
          }`}
          ref={(element) => {
            if (!element) return;
            setContainer(element);
            const { clientWidth, scrollWidth } = element;
            setOverflowActive(scrollWidth > clientWidth);
          }}
        >
          {renderHomeBreadcrumbNode()}
          {items.map((item, index) => {
            return (
              <div
                key={index + item.folderName + "breadcrumbNode"}
                className="flex gap-2"
              >
                /{renderBreadcrumbNode(item, index)}
              </div>
            );
          })}
        </div>

        {/* OVERFLOWED BREADCRUMB */}
        <div
          className={`absolute flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden ${
            overflowActive ? "visible" : "invisible"
          }`}
        >
          {renderHomeBreadcrumbNode()}
          {renderOverflowedBreadcrumb()}
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <div
        className={`w-full h-12 min-h-12 flex flex-row justify-between items-center 
      dark:bg-dark-100 border-b-2 border-grey-300 dark:border-dark-300
      absolute header
      ${visible ? "header--visible" : "header--hidden"} `}
      >
        <div className="flex items-center flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
          {items.length > 0 ? (
            <div
              className="flex justify-center items-center dark:hover:bg-dark-900 hover:bg-grey-200 dark:active:bg-dark-999 active:bg-grey-300 cursor-pointer
        rounded-xl p-2 ml-2"
              onClick={() => onBackClick()}
            >
              <HiArrowLeft className="flex w-4 h-4"></HiArrowLeft>
            </div>
          ) : (
            ""
          )}
          {renderBreadcrumbDropdown(
            <div className="w-full h-full cursor-pointer hover:text-blue-400 ml-1">
              <div>{folder?.name}</div>
            </div>,
            {
              width: "100%",
              offset: 10,
              position: "bottom-start",
            }
          )}
        </div>
        <div className="flex">
          <BottomDrawer
            ctx={{
              folder,
              demo,
              space: currentSpace,
              folderFiles: currentFiles,
            }}
            menuConfig={LOADER_MENU_CONFIG}
            actionsConfig={BOTTOM_DRAWER_QUICK_ACTIONS_CONFIG}
            onActionClick={onActionClick}
            targetElement={
              <div
                className="flex justify-center items-center dark:hover:bg-dark-900 hover:bg-grey-200 dark:active:bg-dark-999 active:bg-grey-300 cursor-pointer
              rounded-xl p-2 mr-2"
              >
                <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
              </div>
            }
          ></BottomDrawer>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-12 flex flex-row justify-between items-center pl-4 rounded-lg dark:bg-dark-100 bg-grey-200 box-border
    ${visible ? "header--visible" : "header--hidden"} absolute header`}
    >
      <div className="flex flex-row items-center gap-2 flex-nowrap text-nowrap max-w-full overflow-hidden">
        {renderContent()}
      </div>

      <div className="flex">
        <LoaderMenu
          ctx={{ folder, demo, space: currentSpace, folderFiles: currentFiles }}
          menuConfig={LOADER_MENU_CONFIG}
          onActionClick={onActionClick}
          targetElement={
            <div
              className="flex justify-center items-center dark:hover:bg-dark-900 hover:bg-grey-200 dark:active:bg-dark-999 active:bg-grey-300 cursor-pointer
        rounded-xl p-2 mr-2"
            >
              <HiDotsVertical className="flex w-4 h-4"></HiDotsVertical>
            </div>
          }
          onOpened={() => {
            setIsLoaderOverlay(true);
          }}
          onClosed={() => {
            setIsLoaderOverlay(false);
          }}
        ></LoaderMenu>
      </div>
    </div>
  );
}
